import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useGetLoginInfo } from '@multiversx/sdk-dapp/hooks/account'
import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { EsdtIssue } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/esdt/issue'
import { useTrackTransactionStatus } from '@multiversx/sdk-dapp/hooks/transactions'
import { getAddress } from '@multiversx/sdk-dapp/utils/account'
import { sendTransactions } from '@multiversx/sdk-dapp/services'

import { useAuthState } from 'src/contexts/authContext'
import { axiosInstance } from 'src/util/axios.js'
import { mvxNetworkProvidersType } from 'src/util/types'
import { mvxNetworkProviders } from 'src/util/networksRpcs'

export interface IUseIssueESDTToken {
  operation: string
  projectType: string
}

export interface IIssueESDTToken {
  txId: string
  address?: string
  tokenTicker: string
  tokenName: string
  initialSupply: number
  numberOfDecimals: number
  network: string
  canMint: boolean
  canBurn: boolean
  canPause: boolean
  canFreeze: boolean
  canWipe: boolean
  canChangeOwner: boolean
  canUpgrade: boolean
  canAddSpecialRoles: boolean
}

const useIssueEsdtToken = ({ operation, projectType }: IUseIssueESDTToken) => {
  const [esdtPayload, setEsdtPayload] = useState<IIssueESDTToken>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { isLoggedIn } = useGetLoginInfo()
  const [txHash, setTxHash] = useState<string>('')
  const navigate = useNavigate()
  const user = useAuthState().user

  let zoid = new ZoidCore({
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  })

  useEffect(() => {
    try {
      const payload = JSON.parse(sessionStorage.getItem('esdtPayload') || '{}')
      setEsdtPayload(payload)
    } catch (err) {
      console.error('err', err)
    }
  }, [])

  const transactionStatus = useTrackTransactionStatus({
    transactionId: esdtPayload?.txId as string,
    onSuccess: async () => {
      setIsLoading(false)
      try {
        const projectResponse = await axiosInstance.post(
          '/blockchain-helpers/external/createProject',
          {
            data: {
              operation,
              chain: 'multiversx',
              network: esdtPayload?.network,
              networkProvider:
                mvxNetworkProviders[esdtPayload?.network as keyof mvxNetworkProvidersType],
              txId: esdtPayload?.txId,
              address: esdtPayload?.address,
              tokenName: esdtPayload?.tokenName,
              tokenTicker: esdtPayload?.tokenTicker,
              initialSupply: Number(esdtPayload?.initialSupply),
              numberOfDecimals: Number(esdtPayload?.numberOfDecimals),
              esdtProperties: {
                canMint: esdtPayload?.canMint,
                canBurn: esdtPayload?.canBurn,
                canPause: esdtPayload?.canPause,
                canFreeze: esdtPayload?.canFreeze,
                canWipe: esdtPayload?.canWipe,
                canChangeOwner: esdtPayload?.canChangeOwner,
                canUpgrade: esdtPayload?.canUpgrade,
                canAddSpecialRoles: esdtPayload?.canAddSpecialRoles,
              },
            },
            txHash,
            projectType,
          },
        )

        sessionStorage.removeItem('esdtPayload')

        navigate(`/projects/${projectResponse?.data?.data?.id}`)
      } catch (err) {
        if (err instanceof Error) {
          toast.error(err?.message, {
            progressClassName: 'simpleBar',
          })
        }
      }
    },
    onFail(transactionId, err) {
      toast.error(`${err} on ${transactionId}`, {
        progressClassName: 'simpleBar',
      })
    },
  })

  useEffect(() => {
    if (transactionStatus?.isPending) {
      if (!txHash) {
        setTxHash(transactionStatus?.transactions?.[0]?.hash || '')
      }
    }
  }, [transactionStatus?.isPending])

  const issueToken = async ({
    tokenTicker,
    tokenName,
    initialSupply,
    numberOfDecimals,
    canMint,
    canBurn,
    canPause,
    canFreeze,
    canWipe,
    canChangeOwner,
    canUpgrade,
    canAddSpecialRoles,
    network,
  }: IIssueESDTToken) => {
    setIsLoading(true)
    try {
      const address = await getAddress()

      const mvxIssuePayload: EsdtIssue = {
        address,
        tokenTicker,
        tokenName,
        initialSupply: Number(initialSupply),
        numberOfDecimals: Number(numberOfDecimals),
        networkProvider: mvxNetworkProviders[network as keyof mvxNetworkProvidersType],
        esdtProperties: {
          canMint,
          canBurn,
          canPause,
          canFreeze,
          canWipe,
          canChangeOwner,
          canUpgrade,
          canAddSpecialRoles,
        },
      }

      const response = await zoid.multiversX.esdtIssueToken(mvxIssuePayload)

      const { transaction, transactionData } = response.data.data

      const transactionRes = await sendTransactions({
        transactions: {
          data: transactionData.data,
          sender: transactionData.sender.bech32,
          receiver: transactionData.receiver.bech32,
          value: transaction.value,
          gasLimit: transaction.gasLimit,
        },
        transactionsDisplayInfo: {
          processingMessage: 'Processing Issue token transaction',
          errorMessage: 'An error has occurred during transaction',
          successMessage: 'Issue token transaction successful',
        },
        redirectAfterSign: false,
      })

      sessionStorage.setItem(
        'esdtPayload',
        JSON.stringify({
          ...mvxIssuePayload,
          network,
          txId: transactionRes?.sessionId,
        }),
      )
    } catch (err) {
      setIsLoading(false)
      if (err instanceof Error) {
        toast.error(err?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  return { isLoading, isWalletConnected: isLoggedIn, issueToken }
}

export default useIssueEsdtToken
